import type {
  DesignPointResponse,
  DesignPointStatus,
  StudyDetailResponse,
  StudyResponse,
  StudyStatus,
} from '@/api/generated/types.gen';
import { canEditStudy } from '@/utils/shareAccess';
import { shortId } from '../../-data/format';
import type { DpMeshingStatus, MeshingDpRow } from './types';

export function isAwaitingUserMeshing(
  study: Pick<StudyResponse | StudyDetailResponse, 'status'>,
): boolean {
  return study.status === 'awaiting_user_meshing';
}

// Journey is Mesh(0) → Simulate(1) → Results(2). There is no Setup step —
// drafts land directly on Simulate, where input picking lives.
const STATUS_TO_JOURNEY_STEP: Record<StudyStatus, number> = {
  draft: 1,
  preprocessing: 0,
  awaiting_user_meshing: 0,
  awaiting_bc_review: 0,
  running: 1,
  awaiting_mobo_review: 2,
  completed: 2,
  completed_with_issues: 2,
  // Failure / cancellation pin to Simulate so the user lands on the
  // design-point inspector (residuals + decoded errors) rather than a locked
  // placeholder. We don't track "died at"; the header status pill and the
  // progress board's failure callout carry the real signal.
  failed: 1,
  cancelled: 1,
};

/** Index (0-based) into `JOURNEY_STEPS` for the study's current status. */
export function statusToJourneyStep(status: StudyStatus): number {
  return STATUS_TO_JOURNEY_STEP[status] ?? 0;
}

export const STEP_SLUGS = ['mesh', 'simulate', 'results'] as const;

export type StepSlug = (typeof STEP_SLUGS)[number];

/**
 * Which journey step a pathname is on, if any.
 *
 * A SEGMENT match, not `endsWith`: the Results step has child routes
 * (`/results/overview`, `/results/points`, `/results/compare`), so the suffix
 * test that served the old flat `/analyze` reported "not on a step" for every
 * one of them — which took the phase breadcrumb, the export affordances and
 * the writeup drawer off the page they belong to.
 *
 * Both library prefixes, because the study detail tree is mounted under
 * `/evaltool/$studyId` as well as `/simops/$studyId` (`STUDY_LIBRARIES`).
 * Matching only SimOps left every EvalTool results URL reading as "not on a
 * step", which is the same bug in a second place.
 */
export function stepSlugFromPath(pathname: string): StepSlug | undefined {
  const slug = /\/(?:simops|evaltool)\/[^/]+\/([^/?#]+)/.exec(pathname)?.[1];
  return (STEP_SLUGS as readonly string[]).includes(slug ?? '')
    ? (slug as StepSlug)
    : undefined;
}

/**
 * Is this pathname the Results step?
 *
 * Shared because two modules act on the answer and must not disagree:
 * `StudyHeader` mounts `AnalyzeHeaderControls` on it, and
 * `report/reportExportAffordances.ts` decides whether that header — and the
 * writeup drawer, mounted only from `results.tsx` — is on the page at all. When
 * those two drifted, the affordance table assigned the export to a control
 * strip that was not rendered and the viewer got no export button.
 *
 * Sniffs the pathname rather than `useMatch({ from: <results route> })`:
 * ENG-1190 bans pinning a route id from a non-route module, because the header
 * can be mounted under more than one route.
 */
export function isResultsPath(pathname: string): boolean {
  return stepSlugFromPath(pathname) === 'results';
}

/** Step slug for the URL that matches the study's current status. */
export function statusToStepSlug(status: StudyStatus): StepSlug {
  return STEP_SLUGS[statusToJourneyStep(status)];
}

/**
 * The step a reader forwarded into the workbench from a share link lands on
 * (ENG-2858).
 *
 * Any completed design point makes it Results, so a running study that
 * already has something to read opens on it. Without one it is the status's
 * own step: Results has nothing to show and renders its "not ready"
 * placeholder, which is the dead end this exists to avoid. Not Simulate
 * unconditionally either — a study still meshing or in BC review gets
 * Simulate's own "not yet" placeholder there, and has a live view on Mesh.
 */
export function shareLandingStepSlug(
  status: StudyStatus,
  hasResults: boolean,
): StepSlug {
  return hasResults ? 'results' : statusToStepSlug(status);
}

/**
 * Forward-looking: a study that timed out in `AWAITING_USER_MESHING` after 7
 * days lands here. Backend will surface this via a `failure_reason` field
 * (planned, not yet in the schema) on `failed` studies; until then this
 * always returns false — the `TimeoutCard` stays dormant.
 *
 * TODO: align the `'meshing_timeout'` literal with the backend enum once
 * `failure_reason` lands; if the name diverges this predicate will silently
 * keep returning false.
 */
export function isMeshingTimedOut(
  study: Pick<StudyResponse | StudyDetailResponse, 'status'> & {
    failure_reason?: string | null;
  },
): boolean {
  if (study.status !== 'failed') return false;
  const reason = study.failure_reason;
  return reason === 'meshing_timeout';
}

/**
 * Project a design-point's sim-side status onto the user-meshing lifecycle.
 * `mesh_ready` = mesh produced, faces not yet named; `faces_done` = named,
 * ready to simulate. A point already simulating or beyond got there by
 * finishing its mesh, so it reads `faces_done` too — the read-only Mesh step
 * of a study past meshing lists exactly those. None of them occur while a
 * study is in user meshing, so that phase reads as before. Anything else
 * collapses to `not_started`.
 */
const DP_MESHING_STATUS_MAP: Partial<
  Record<DesignPointStatus, DpMeshingStatus>
> = {
  mesh_ready: 'mesh_ready',
  faces_done: 'faces_done',
  meshing: 'meshing',
  simulating: 'faces_done',
  extracting: 'faces_done',
  pending_review: 'faces_done',
  completed: 'faces_done',
  failed: 'failed',
  cancelled: 'failed',
};

export function dpMeshingStatusFor(dp: DesignPointResponse): DpMeshingStatus {
  // A failed or cancelled point that has a simulation failed AFTER meshing:
  // simulations are only created once the mesh is done. Reading it `failed`
  // here would put "Failed in mesh" beside a mesh that is fine.
  if (
    (dp.status === 'failed' || dp.status === 'cancelled') &&
    (dp.simulations?.length ?? 0) > 0
  ) {
    return 'faces_done';
  }
  return DP_MESHING_STATUS_MAP[dp.status] ?? 'not_started';
}

/**
 * Whether the study's meshes are shown read-only — on the Mesh step and in
 * MeshGen opened from it (`?study=&dp=`).
 *
 * Editable only while the study is waiting on the user's meshes AND the
 * reader can edit the study. Past that phase a mesh edit cannot reach runs
 * that have already been set up, and "Complete meshing & run" has nothing
 * left to complete; a viewer or commenter could never run either. Read-only
 * rather than locked: the meshes stay open to look at.
 */
export function isStudyMeshingReadOnly(study: StudyDetailResponse): boolean {
  return !isAwaitingUserMeshing(study) || !canEditStudy(study);
}

/** True when a DP has both a mesh and all faces named — ready to simulate. */
export function isFacesDone(dp: { status: DpMeshingStatus }): boolean {
  return dp.status === 'faces_done';
}

/** True when a DP's mesh survives a study cancel — produced or fully named. */
export function isMeshPreserved(dp: { status: DpMeshingStatus }): boolean {
  return dp.status === 'mesh_ready' || dp.status === 'faces_done';
}

export function buildMeshingRow(
  dp: DesignPointResponse,
  geomName: string,
  variant: string,
): MeshingDpRow {
  return {
    id: dp.id,
    dp: shortId(dp.id),
    geom: geomName,
    variant,
    status: dpMeshingStatusFor(dp),
  };
}
